import { useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getMembership, MEMBERSHIP_QUERY_KEY } from '../api/endpoints/membership';
import { BILLING_QUERY_KEY } from '../api/endpoints/planPayment';
import { getStripeStatus, STRIPE_STATUS_QUERY_KEY, type StripeStatus } from '../api/endpoints/stripe';
import { SessionContext } from '../auth/SessionContext';
import { formatTrialDate, holdsFirstCharge, isTrialRunning } from '../lib/trial';
import { CardStep } from './CardStep';
import { ErrorBanner } from './ErrorBanner';
import { LoadingState } from './LoadingState';
import { ResultPanel } from './PagoResultPanel';

/**
 * Stripe on /pago. `CardLocked` is the card step a new signup is kept on,
 * once its free trial is over, until Stripe confirms its card; `CardTrial` is
 * the same step while the trial runs, optional and charging nothing until the
 * trial ends; `StripeReturn` is where Stripe sends the
 * owner back (?stripe=listo) and waits, asking the server a few times, for
 * the confirmation to land (the webhook, or the server asking Stripe itself).
 */

/** How often and how many times the return asks before saying it is taking long. */
export const STRIPE_POLL_MS = 2_000;
export const STRIPE_MAX_POLLS = 8;

const CANCELLED_TEXT = 'Cancelaste en Stripe. Tu tarjeta no se guardó; puedes intentarlo de nuevo cuando quieras.';
const MEMBERSHIP_ERROR_TEXT = 'No pudimos cargar tu plan. Intenta de nuevo.';

/** True once Stripe confirmed a card and nothing the owner started is still open: the owner's panel opens. */
export function isCardConfirmed(status: StripeStatus | undefined): boolean {
  return Boolean(status && status.cardOnFile && !status.cardRequired && !status.checkoutOpen);
}

/** Tells the session the card is confirmed, once, so the panel stops sending the owner here and stops counting down. */
function useConfirmCardWhen(confirmed: boolean) {
  const context = useContext(SessionContext);
  const pending = Boolean(context?.session?.cardRequired) || Boolean(context?.session?.trialEndsAt);
  useEffect(() => {
    if (confirmed && pending) context?.confirmCard?.();
  }, [confirmed, pending, context]);
}

/** The plan on file, then the card step for it; what both the locked and the trial step show. */
function PlanCardStep({ freeUntil }: { freeUntil?: string }) {
  const membership = useQuery({ queryKey: MEMBERSHIP_QUERY_KEY, queryFn: getMembership, retry: false });
  if (membership.isPending) return <LoadingState label="Cargando tu plan…" />;
  if (membership.isError) {
    return (
      <>
        <ErrorBanner message={MEMBERSHIP_ERROR_TEXT} />
        <button type="button" className="btn secondary" onClick={() => void membership.refetch()}>
          Reintentar
        </button>
      </>
    );
  }
  const { plan, billingCycle, monthlyPriceCents } = membership.data;
  return <CardStep plan={plan} cycle={billingCycle} monthlyPriceCents={monthlyPriceCents} {...(freeUntil ? { freeUntil } : {})} />;
}

function CancelledNotice({ cancelled }: { cancelled: boolean }) {
  if (!cancelled) return null;
  return (
    <p className="pago-notice" role="status">
      {CANCELLED_TEXT}
    </p>
  );
}

/** Asked in the background: a card confirmed while the owner was away opens the panel. */
function useStripeStatus() {
  const status = useQuery({ queryKey: STRIPE_STATUS_QUERY_KEY, queryFn: getStripeStatus, retry: false });
  useConfirmCardWhen(isCardConfirmed(status.data));
  return status;
}

export function CardLocked({ cancelled }: { cancelled: boolean }) {
  const status = useStripeStatus();
  const trialEndsAt = status.data?.trialEndsAt;
  const lede = trialEndsAt
    ? `Tu prueba gratis terminó el ${formatTrialDate(trialEndsAt)}. Agrega tu tarjeta para seguir usando tu panel.`
    : 'Tu negocio ya está creado. Agrega tu tarjeta y empiezas a usar Dale.';

  return (
    <div className="pago-intro">
      <h1>Activa tu panel</h1>
      <p className="lede">{lede}</p>
      <CancelledNotice cancelled={cancelled} />
      <PlanCardStep />
    </div>
  );
}

export function CardTrial({ trialEndsAt, cancelled }: { trialEndsAt: string; cancelled: boolean }) {
  useStripeStatus();
  const until = formatTrialDate(trialEndsAt);

  return (
    <div className="pago-intro">
      <h1>Gratis hasta el {until}</h1>
      <p className="lede">Tu panel está abierto y todavía no necesitas tarjeta. Si la guardas ahora, sigues sin pausa cuando termine la prueba.</p>
      <CancelledNotice cancelled={cancelled} />
      <PlanCardStep {...(holdsFirstCharge(trialEndsAt) ? { freeUntil: until } : {})} />
    </div>
  );
}

/** What the confirmation says was set up: a trial subscription, or a paid one. */
function confirmedLede(status: StripeStatus | undefined): string {
  if (status?.subscribed && isTrialRunning(status.trialEndsAt)) {
    return `Guardamos tu tarjeta. No te cobramos nada hasta el ${formatTrialDate(status.trialEndsAt!)}; desde ese día tu plan se renueva solo con esta tarjeta.`;
  }
  return 'Tu plan de Dale quedó pagado y se renueva solo con esta tarjeta. Te enviamos la confirmación por correo.';
}

export type StripeReturnProps = {
  fromSignup: boolean;
  onContinue: () => void;
  /** Test seams; the page uses STRIPE_POLL_MS and STRIPE_MAX_POLLS. */
  pollMs?: number;
  maxPolls?: number;
};

export function StripeReturn({ fromSignup, onContinue, pollMs = STRIPE_POLL_MS, maxPolls = STRIPE_MAX_POLLS }: StripeReturnProps) {
  const client = useQueryClient();
  const [round, setRound] = useState(0);
  const queryKey = [...STRIPE_STATUS_QUERY_KEY, 'return', round];
  const status = useQuery({
    queryKey,
    queryFn: getStripeStatus,
    retry: false,
    refetchInterval: (query) => (isCardConfirmed(query.state.data) || query.state.dataUpdateCount >= maxPolls ? false : pollMs),
    // Re-render on every answer, even one identical to the last, so the count of answers below is current.
    notifyOnChangeProps: 'all',
  });
  const confirmed = isCardConfirmed(status.data);
  useConfirmCardWhen(confirmed);
  useEffect(() => {
    if (confirmed) void client.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
  }, [confirmed, client]);

  const continueLabel = fromSignup ? 'Ir a mi panel' : 'Volver a Membresía';
  if (confirmed) {
    return (
      <ResultPanel
        glyph="check"
        title="Tu tarjeta quedó lista"
        actions={
          <button type="button" className="btn" onClick={onContinue}>
            {continueLabel}
          </button>
        }
      >
        <p className="pago-result-lede">{confirmedLede(status.data)}</p>
      </ResultPanel>
    );
  }

  const answers = client.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  const stillAsking = !status.isError && (status.data === undefined || answers < maxPolls);
  if (stillAsking) {
    return (
      <ResultPanel glyph="clock" title="Confirmando tu tarjeta" actions={null}>
        <p className="pago-result-lede">Stripe nos está confirmando tu tarjeta. Esto toma unos segundos, no cierres esta página.</p>
      </ResultPanel>
    );
  }

  return (
    <ResultPanel
      glyph="info"
      title="Esto está tardando más de lo normal"
      actions={
        <button type="button" className="btn" onClick={() => setRound((value) => value + 1)}>
          Revisar de nuevo
        </button>
      }
    >
      <p className="pago-result-lede">
        Stripe todavía no nos confirma tu tarjeta. Si ya la agregaste, espera un minuto y revisa de nuevo. Si el problema sigue, escríbenos.
      </p>
    </ResultPanel>
  );
}
